import { useCallback, useEffect, useRef } from 'react';
import { useProxyStore } from '../store/proxy-store';
import { useProjectStore } from '../store/projectStore';
import {
  captureMatchReplacePersistSnapshot,
  type MatchReplacePersistSnapshot,
} from '../utils/match-replace-persistence';
import { persistMatchReplaceSnapshotToServer } from '../utils/persistMatchReplaceRules';

const PERSIST_DEBOUNCE_MS = 300;

export function useMatchReplacePersistence({
  onPersistError,
  onPersistSuccess,
}: {
  onPersistError?: (params: { message: string }) => void;
  onPersistSuccess?: () => void;
} = {}) {
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistQueueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingSnapshotRef = useRef<MatchReplacePersistSnapshot | null>(null);
  const selectedApplicationId = useProjectStore(
    (state) => state.selectedTarget?.id,
  );

  const cancelPendingPersist = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    pendingSnapshotRef.current = null;
  }, []);

  useEffect(() => {
    cancelPendingPersist();
    return () => {
      cancelPendingPersist();
    };
  }, [selectedApplicationId, cancelPendingPersist]);

  const executePersist = useCallback(
    async (snapshot: MatchReplacePersistSnapshot) => {
      const result = await persistMatchReplaceSnapshotToServer(snapshot);
      if (result.success) {
        onPersistSuccess?.();
        return;
      }

      onPersistError?.({ message: result.error });
    },
    [onPersistError, onPersistSuccess],
  );

  const enqueuePersist = useCallback(
    (snapshot: MatchReplacePersistSnapshot) => {
      persistQueueRef.current = persistQueueRef.current
        .catch((error: unknown) => {
          console.warn('Match/replace persist queue failed', {
            error: error instanceof Error ? error : new Error(String(error)),
          });
        })
        .then(async () => {
          await executePersist(snapshot);
        });
      return persistQueueRef.current;
    },
    [executePersist],
  );

  const captureSnapshot =
    useCallback((): MatchReplacePersistSnapshot | null => {
      const applicationId = useProjectStore.getState().selectedTarget?.id;
      const { settings } = useProxyStore.getState();
      return captureMatchReplacePersistSnapshot({
        applicationId,
        rules: settings.matchReplaceRules || [],
        matchReplaceEnabled: settings.matchReplaceEnabled,
      });
    }, []);

  const schedulePersistRulesToServer = useCallback(() => {
    const snapshot = captureSnapshot();
    if (!snapshot) {
      return;
    }

    pendingSnapshotRef.current = snapshot;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const pendingSnapshot = pendingSnapshotRef.current;
      pendingSnapshotRef.current = null;
      if (!pendingSnapshot) {
        return;
      }
      void enqueuePersist(pendingSnapshot);
    }, PERSIST_DEBOUNCE_MS);
  }, [captureSnapshot, enqueuePersist]);

  const persistRulesToServerNow = useCallback(async () => {
    const snapshot = captureSnapshot();
    if (!snapshot) {
      return;
    }

    cancelPendingPersist();
    await enqueuePersist(snapshot);
  }, [captureSnapshot, enqueuePersist, cancelPendingPersist]);

  return {
    schedulePersistRulesToServer,
    persistRulesToServerNow,
    cancelPendingPersist,
  };
}
